import type { Schema } from '@/services/api/schemas'

/**
 * A schema reduced to its portable *definition* — the fields that describe the
 * schema, with the server-managed instance metadata (id, version, is_active,
 * timestamps, the list-row `*_count`s) stripped. Shaped to round-trip through
 * `POST /v1/schemas/import`.
 */
export interface SchemaExport {
  name: string
  description?: string
  additional_node_types: boolean
  additional_relationship_types: boolean
  additional_patterns: boolean
  additional_properties_allowed: boolean
  entity_types: Schema['entity_types']
  relationship_types: Schema['relationship_types']
  patterns: Schema['patterns']
}

/** Strip a loaded schema down to its portable definition (see {@link SchemaExport}). */
export function schemaToExport(schema: Schema): SchemaExport {
  return {
    name: schema.name,
    description: schema.description,
    additional_node_types: schema.additional_node_types,
    additional_relationship_types: schema.additional_relationship_types,
    additional_patterns: schema.additional_patterns,
    additional_properties_allowed: schema.additional_properties_allowed,
    entity_types: schema.entity_types,
    relationship_types: schema.relationship_types,
    patterns: schema.patterns,
  }
}

/** Pretty-printed JSON of the schema's portable definition. */
export function schemaToExportJson(schema: Schema): string {
  return JSON.stringify(schemaToExport(schema), null, 2)
}

/** Safe download filename for a schema export, e.g. `My Schema` v3 → `My_Schema-v3.json`. */
export function schemaExportFilename(schema: Schema): string {
  const safe =
    schema.name
      .trim()
      .replace(/[^\w.-]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'schema'
  return `${safe}-v${schema.version}.json`
}
